import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { CONTACT } from "@/lib/data";

const LINKS = [
  { to: "/services", label: "Services" },
  { to: "/shippers", label: "Shippers" },
  { to: "/carriers", label: "Carriers" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function LogoMark({ size = 34 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <rect width="64" height="64" rx="12" fill="#15181E" stroke="#282D38" />
      <path d="M13 47 L30 17 L51 47" fill="none" stroke="#FF5500" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="35" y1="31" x2="39" y2="38" stroke="#E5A93C" strokeWidth="3.5" strokeLinecap="round" strokeDasharray="5 6" />
      <circle cx="51" cy="47" r="4.5" fill="#E5A93C" />
    </svg>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-line bg-asphalt/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          <LogoMark />
          <span className="font-display text-xl font-800 font-extrabold uppercase tracking-wide text-bright">
            ELT <span className="text-hazard">Freight</span> &amp; Logistics
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
                  isActive ? "text-hazard" : "text-[#9CA3AF] hover:text-bright"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            data-testid="nav-phone-link"
            href={CONTACT.phoneHref}
            className="hidden items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-[#9CA3AF] transition-colors hover:text-hazard xl:flex"
          >
            <Phone size={13} className="text-hazard" /> {CONTACT.phone}
          </a>
          <button
            data-testid="nav-quote-btn"
            onClick={() => navigate("/shippers")}
            className="group flex items-center gap-1.5 bg-hazard px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.15em] text-asphalt transition-colors duration-300 hover:bg-hazard-dark"
          >
            Request a Quote
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="text-bright lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-line bg-asphalt/95 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-1 px-5 py-4">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `border-l-2 px-3 py-3 font-mono text-sm uppercase tracking-[0.18em] ${
                    isActive ? "border-hazard text-hazard" : "border-transparent text-[#9CA3AF]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              data-testid="nav-mobile-quote-btn"
              onClick={() => {
                setOpen(false);
                navigate("/shippers");
              }}
              className="mt-2 bg-hazard px-4 py-3 font-mono text-sm font-bold uppercase tracking-[0.15em] text-asphalt"
            >
              Request a Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
